import { z } from 'zod'
import {
  CLIENT_SOURCES,
  CLIENT_STATUSES,
  CURRENCIES,
  EDUCATION_LEVELS,
  GENDERS,
  LANGUAGE_PROFICIENCIES,
  LANGUAGES,
  MARITAL_STATUSES,
  PASSPORT_STATUSES,
} from '../constants/clients.js'
import { isCountryCode } from '../constants/countries.js'
import { DISTRICTS } from '../constants/districts.js'

/**
 * Country codes are validated against the managed list rather than a z.enum:
 * COUNTRY_CODES is derived with `.map`, so it's a plain `string[]` and z.enum
 * would silently accept *any* string. A refine actually checks membership.
 */
export const countryCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine(isCountryCode, 'Unknown country')

/**
 * Phone numbers are normalised to E.164 on the way in, because duplicate
 * detection compares them as strings: `077 123 4567`, `0771234567` and
 * `+94771234567` are one person, and storing them verbatim would let the same
 * client register three times.
 *
 * A bare local number is assumed Sri Lankan (the agency's market); anything
 * already carrying a `+` is kept as-is so foreign numbers still work.
 */
export function normalisePhone(input: string): string {
  const trimmed = input.trim().replace(/[\s\-().]/g, '')
  if (trimmed.startsWith('+')) return trimmed
  if (trimmed.startsWith('00')) return `+${trimmed.slice(2)}`
  if (trimmed.startsWith('0')) return `+94${trimmed.slice(1)}`
  if (trimmed.startsWith('94')) return `+${trimmed}`
  return trimmed
}

export const phoneSchema = z
  .string()
  .trim()
  .min(1, 'Phone number is required')
  .transform(normalisePhone)
  .refine((v) => /^\+\d{9,15}$/.test(v), 'Enter a valid phone number, e.g. 077 123 4567')

export const optionalPhoneSchema = z
  .union([z.literal(''), phoneSchema])
  .optional()
  .transform((v) => (v === '' ? undefined : v))

/**
 * Sri Lankan NIC: the old form is 9 digits plus V or X, the new form is 12
 * digits. Normalised to uppercase so `123456789v` and `123456789V` collide in
 * the duplicate check rather than creating two records.
 */
export function normaliseNic(input: string): string {
  return input.trim().toUpperCase().replace(/\s/g, '')
}

export const nicSchema = z
  .string()
  .trim()
  .transform(normaliseNic)
  .refine(
    (v) => /^(\d{9}[VX]|\d{12})$/.test(v),
    'Enter a valid NIC, e.g. 912345678V or 199123456789',
  )

export const optionalNicSchema = z
  .union([z.literal(''), nicSchema])
  .optional()
  .transform((v) => (v === '' ? undefined : v))

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the date picker')
  .refine((v) => !Number.isNaN(Date.parse(v)), 'Not a real date')

const optionalIsoDate = z
  .union([z.literal(''), isoDate])
  .optional()
  .transform((v) => (v === '' ? undefined : v))

const optionalText = (max: number) =>
  z
    .union([z.literal(''), z.string().trim().max(max)])
    .optional()
    .transform((v) => (v === '' ? undefined : v))

const optionalEmail = z
  .union([z.literal(''), z.string().trim().toLowerCase().email('Enter a valid email').max(255)])
  .optional()
  .transform((v) => (v === '' ? undefined : v))

export const languageEntrySchema = z.object({
  language: z.enum(LANGUAGES),
  proficiency: z.enum(LANGUAGE_PROFICIENCIES),
})
export type LanguageEntry = z.infer<typeof languageEntrySchema>

export const foreignExperienceEntrySchema = z.object({
  countryCode: countryCodeSchema,
  role: z.string().trim().min(1, 'Role is required').max(100),
  years: z.coerce.number().min(0).max(60),
})
export type ForeignExperienceEntry = z.infer<typeof foreignExperienceEntrySchema>

const MIN_AGE = 16
const MAX_AGE = 75

export const clientBaseSchema = z.object({
  // Identity
  fullName: z.string().trim().min(2, 'Full name is required').max(150),
  initialsName: optionalText(100),
  dob: isoDate
    .refine((v) => {
      const age = ageFromDob(v)
      return age >= MIN_AGE && age <= MAX_AGE
    }, `Age must be between ${MIN_AGE} and ${MAX_AGE}`)
    .optional(),
  gender: z.enum(GENDERS).optional(),
  maritalStatus: z.enum(MARITAL_STATUSES).optional(),
  nic: optionalNicSchema,
  nationality: optionalText(60),
  religion: optionalText(60),

  // Contact
  phone: phoneSchema,
  whatsapp: optionalPhoneSchema,
  email: optionalEmail,
  address: optionalText(255),
  city: optionalText(80),
  district: z.enum(DISTRICTS).optional(),
  emergencyName: optionalText(150),
  emergencyPhone: optionalPhoneSchema,

  // Passport
  passportStatus: z.enum(PASSPORT_STATUSES).default('none'),
  passportNo: optionalText(30),
  passportIssued: optionalIsoDate,
  passportExpiry: optionalIsoDate,

  // Professional
  educationLevel: z.enum(EDUCATION_LEVELS).optional(),
  experienceYears: z.coerce.number().min(0).max(60).optional(),
  jobCategoryIds: z.array(z.string()).max(10, 'Pick at most 10 job categories').default([]),
  languages: z.array(languageEntrySchema).max(8).default([]),
  foreignExperience: z.array(foreignExperienceEntrySchema).max(10).default([]),

  // Preferences
  preferredCountries: z.array(countryCodeSchema).max(10, 'Pick at most 10 countries').default([]),
  expectedSalary: z.coerce.number().min(0).max(10_000_000).optional(),
  salaryCurrency: z.enum(CURRENCIES).optional(),
  availableFrom: optionalIsoDate,

  // Physical
  heightCm: z.coerce.number().int().min(100).max(250).optional(),
  weightKg: z.coerce.number().int().min(30).max(250).optional(),

  // Agency-side
  assignedUserId: optionalText(60),
  consentSms: z.boolean().default(false),
  consentWhatsapp: z.boolean().default(false),
  consentEmail: z.boolean().default(false),
})

/** A passport can't expire before it was issued, and "in hand" needs a number. */
const passportRules = <T extends z.ZodTypeAny>(schema: T) =>
  schema
    .superRefine((v: z.infer<typeof clientBaseSchema>, ctx: z.RefinementCtx) => {
      if (v.passportIssued && v.passportExpiry && v.passportExpiry < v.passportIssued) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['passportExpiry'],
          message: 'Expiry cannot be before the issue date',
        })
      }
      if (v.passportStatus === 'in_hand' && !v.passportNo) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['passportNo'],
          message: 'Passport number is required when the passport is in hand',
        })
      }
    })
    .transform((v: z.infer<typeof clientBaseSchema>) => v)

export const createClientSchema = passportRules(clientBaseSchema)
export type CreateClientInput = z.input<typeof clientBaseSchema>
export type CreateClientParsed = z.output<typeof clientBaseSchema>

export const updateClientSchema = clientBaseSchema.partial().extend({
  status: z.enum(CLIENT_STATUSES).optional(),
})
export type UpdateClientInput = z.infer<typeof updateClientSchema>

export function ageFromDob(dob: string): number {
  const birth = new Date(dob)
  const now = new Date()
  let age = now.getUTCFullYear() - birth.getUTCFullYear()
  const monthDiff = now.getUTCMonth() - birth.getUTCMonth()
  if (monthDiff < 0 || (monthDiff === 0 && now.getUTCDate() < birth.getUTCDate())) age--
  return age
}

/* -------------------------------------------------------------------------- */
/*  List query                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Multi-value filters travel as comma-separated strings (`?status=active,confirmed`)
 * rather than repeated keys — shorter URLs, and one parse path shared by the
 * router's search params and the API's query validation.
 */
const csv = <T extends string>(values: readonly T[]) =>
  z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => {
      if (v === undefined) return undefined
      const list = Array.isArray(v) ? v : v.split(',').filter(Boolean)
      return list.length > 0 ? list : undefined
    })
    .pipe(z.array(z.enum(values as unknown as [T, ...T[]])).optional())

/** Comma-separated list of country codes, checked against the managed list. */
const csvCountries = z
  .union([z.string(), z.array(z.string())])
  .optional()
  .transform((v) => {
    if (v === undefined) return undefined
    const list = Array.isArray(v) ? v : v.split(',').filter(Boolean)
    return list.length > 0 ? list : undefined
  })
  .pipe(z.array(countryCodeSchema).optional())

export const PAGE_SIZES = [25, 50, 100] as const

export const listClientsQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce
    .number()
    .int()
    .refine((v) => (PAGE_SIZES as readonly number[]).includes(v), 'Invalid page size')
    .default(25),

  search: z.string().trim().max(100).optional(),

  status: csv(CLIENT_STATUSES),
  source: csv(CLIENT_SOURCES),
  country: csvCountries,
  category: z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => {
      if (v === undefined) return undefined
      const list = Array.isArray(v) ? v : v.split(',').filter(Boolean)
      return list.length > 0 ? list : undefined
    }),
  district: csv(DISTRICTS),
  gender: csv(GENDERS),
  passportStatus: csv(PASSPORT_STATUSES),

  ageMin: z.coerce.number().int().min(MIN_AGE).max(MAX_AGE).optional(),
  ageMax: z.coerce.number().int().min(MIN_AGE).max(MAX_AGE).optional(),

  assignedUserId: z.string().optional(),
  registrationLinkId: z.string().optional(),

  createdFrom: optionalIsoDate,
  createdTo: optionalIsoDate,

  /** Admin-only escape hatch for reviewing soft-deleted records. */
  includeDeleted: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .optional()
    .transform((v) => v === true || v === 'true'),
})
export type ListClientsQuery = z.infer<typeof listClientsQuerySchema>

/** Just the filters — what a bulk action or campaign audience is defined by. */
export const clientFilterSchema = listClientsQuerySchema.omit({ cursor: true, limit: true })
export type ClientFilter = z.infer<typeof clientFilterSchema>

/* -------------------------------------------------------------------------- */
/*  Bulk actions                                                               */
/* -------------------------------------------------------------------------- */

/**
 * `confirm` and `reject` are separate from `set_status` even though both end in
 * a status change: they're review decisions, they must only ever apply to
 * pending registrations, and they audit differently (reject carries a reason).
 * Folding them into `set_status` would let a stray call "confirm" a blacklisted
 * client.
 */
export const BULK_ACTIONS = ['set_status', 'assign', 'delete', 'confirm', 'reject'] as const
export type BulkAction = (typeof BULK_ACTIONS)[number]

/**
 * A bulk action targets either an explicit list of ids (what's ticked on
 * screen) or a filter (everything matching, including rows the user never
 * scrolled to). The filter form is what makes "confirm all 1,200 matches"
 * possible without shipping 1,200 ids to the server.
 */
export const bulkClientsSchema = z
  .object({
    action: z.enum(BULK_ACTIONS),
    status: z.enum(CLIENT_STATUSES).optional(),
    assignedUserId: z.string().nullable().optional(),
    reason: z.string().trim().max(500).optional(),
    ids: z.array(z.string()).min(1).max(500).optional(),
    filter: clientFilterSchema.optional(),
    /** Client-side count at confirm time; a mismatch means the set moved under them. */
    expectedCount: z.number().int().min(0).optional(),
  })
  .refine((v) => Boolean(v.ids) !== Boolean(v.filter), {
    message: 'Provide exactly one of ids or filter',
  })
  .refine((v) => v.action !== 'set_status' || v.status !== undefined, {
    path: ['status'],
    message: 'status is required for set_status',
  })
  .refine((v) => v.action !== 'assign' || v.assignedUserId !== undefined, {
    path: ['assignedUserId'],
    message: 'assignedUserId is required for assign',
  })
  .refine((v) => v.action !== 'reject' || (v.reason?.length ?? 0) > 0, {
    path: ['reason'],
    message: 'A reason is required when rejecting',
  })
export type BulkClientsInput = z.infer<typeof bulkClientsSchema>

/* -------------------------------------------------------------------------- */
/*  Duplicates & notes                                                         */
/* -------------------------------------------------------------------------- */

export const duplicateQuerySchema = z
  .object({
    nic: z.string().trim().optional(),
    phone: z.string().trim().optional(),
    passportNo: z.string().trim().optional(),
    excludeId: z.string().optional(),
  })
  .refine((v) => v.nic || v.phone || v.passportNo, {
    message: 'Provide at least one of nic, phone or passportNo',
  })
export type DuplicateQuery = z.infer<typeof duplicateQuerySchema>

export const createNoteSchema = z.object({
  body: z.string().trim().min(1, 'Note cannot be empty').max(2000),
})
export type CreateNoteInput = z.infer<typeof createNoteSchema>

/* -------------------------------------------------------------------------- */
/*  PII masking                                                                */
/* -------------------------------------------------------------------------- */

/**
 * List views show masked identifiers; the full value appears on the profile
 * (PLAN §11). Masking here rather than in the UI means the *API* never sends
 * the full NIC to a list request at all — you can't leak what you didn't send.
 */
export function maskNic(nic: string | null | undefined): string | null {
  if (!nic) return null
  if (nic.length <= 4) return '••••'
  return `${'•'.repeat(nic.length - 4)}${nic.slice(-4)}`
}

export function maskPassport(passportNo: string | null | undefined): string | null {
  if (!passportNo) return null
  if (passportNo.length <= 3) return '•••'
  return `${passportNo.slice(0, 1)}${'•'.repeat(passportNo.length - 3)}${passportNo.slice(-2)}`
}
